import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Search, Music, Play, Pause, Upload, X } from "lucide-react";
import type { TimelineAudioTrack, TimelineAction } from "@scroll/shared-types/timeline";
import { getTotalDuration, generateId, formatTimeShort } from "@scroll/shared-types/timeline-utils";
import type { TimelineProject } from "@scroll/shared-types/timeline";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { cancelUploadedAudio, uploadTimelineAudioFile } from "@/lib/timeline-upload";

const MAX_AUDIO_BYTES = 50 * 1024 * 1024;

function loadAudioMetadata(url: string): Promise<{ duration: number }> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => resolve({ duration: audio.duration });
    audio.onerror = () => reject(new Error("Audio metadata could not be loaded"));
    audio.src = url;
  });
}

interface MusicTrack {
  id: number;
  title: string;
  artist: string;
  duration: number;
  genre?: string | null;
  audioUrl: string;
  coverArtUrl?: string | null;
}

interface MusicPickerDrawerProps {
  project: TimelineProject;
  onDispatch: (action: TimelineAction) => void;
  onClose: () => void;
}

const GENRES = ["Chill", "Upbeat", "Cinematic", "Lo-fi", "Electronic", "Acoustic"];

export default function MusicPickerDrawer({
  project,
  onDispatch,
  onClose,
}: MusicPickerDrawerProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<number | null>(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const { data: tracks = [], isLoading } = useQuery<MusicTrack[]>({
    queryKey: ["/api/music/tracks", search, genre],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (genre) params.set("genre", genre.toLowerCase());
      params.set("limit", "20");

      const res = await fetch(`/api/music/tracks?${params}`, { credentials: "include" });
      if (!res.ok) return [];
      // The endpoint returns `{ tracks: MusicTrack[], hasMore: boolean }` —
      // unwrap. Be defensive about a bare array in case the shape ever changes.
      const body = await res.json();
      if (Array.isArray(body)) return body;
      return Array.isArray(body?.tracks) ? body.tracks : [];
    },
  });

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current = null;
      }
    };
  }, []);

  const togglePreview = useCallback((track: MusicTrack) => {
    if (previewingId === track.id) {
      audioRef.current?.pause();
      setPreviewingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
    }
    const audio = new Audio(track.audioUrl);
    audio.volume = 0.5;
    audio.play();
    audio.onended = () => setPreviewingId(null);
    audioRef.current = audio;
    setPreviewingId(track.id);
  }, [previewingId]);

  const handleUploadAudio = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUploadingAudio) return;
      const input = e.currentTarget;
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("audio/") && !/\.(mp3|m4a|wav|ogg)$/i.test(file.name)) {
        toast({
          title: "Unsupported file",
          description: "Pick an MP3, M4A, WAV, or OGG file.",
          variant: "destructive",
        });
        input.value = "";
        return;
      }
      if (file.size > MAX_AUDIO_BYTES) {
        toast({
          title: "Audio is too large",
          description: "Audio uploads are capped at 50 MB. Compress the file and try again.",
          variant: "destructive",
        });
        input.value = "";
        return;
      }
      if (!user?.id) {
        toast({
          title: "Sign in required",
          description: "Please sign in before uploading audio.",
          variant: "destructive",
        });
        input.value = "";
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      setIsUploadingAudio(true);
      try {
        const metadata = await loadAudioMetadata(objectUrl);
        const uploaded = await uploadTimelineAudioFile(file, user.id);
        if (!isMountedRef.current) {
          void cancelUploadedAudio(uploaded.blobKey);
          return;
        }
        const totalDuration = getTotalDuration(project);
        const track: TimelineAudioTrack = {
          id: generateId(),
          type: "music",
          sourceUri: uploaded.blobUrl,
          sourceDuration: metadata.duration,
          timelineStart: 0,
          trimStart: 0,
          trimEnd: Math.min(metadata.duration, totalDuration || metadata.duration),
          volumeKeyframes: [{ time: 0, value: 0.7 }],
          title: file.name.replace(/\.[^/.]+$/, ""),
          blobKey: uploaded.blobKey,
          blobUrl: uploaded.blobUrl,
        };
        onDispatch({ type: "ADD_AUDIO_TRACK", track });
        onClose();
      } catch (err) {
        console.warn("[timeline-publish] audio upload failed", {
          fileType: file.type,
          fileSize: file.size,
          error: err instanceof Error ? err.message : String(err),
        });
        if (!isMountedRef.current) return;
        const reason = err instanceof Error ? err.message : "Please try uploading that audio again.";
        toast({
          title: "Audio upload failed",
          description: reason,
          variant: "destructive",
        });
      } finally {
        URL.revokeObjectURL(objectUrl);
        if (isMountedRef.current) setIsUploadingAudio(false);
        input.value = "";
      }
    },
    [isUploadingAudio, onClose, onDispatch, project, toast, user?.id],
  );

  const handleSelect = useCallback((track: MusicTrack) => {
    // Stop preview
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewingId(null);

    const totalDuration = getTotalDuration(project);
    const audioTrack: TimelineAudioTrack = {
      id: generateId(),
      type: "music",
      sourceUri: track.audioUrl,
      sourceDuration: track.duration,
      timelineStart: 0,
      trimStart: 0,
      trimEnd: Math.min(track.duration, totalDuration),
      volumeKeyframes: [{ time: 0, value: 0.7 }],
      musicTrackId: track.id,
      title: track.title,
      artist: track.artist,
    };

    onDispatch({ type: "ADD_AUDIO_TRACK", track: audioTrack });
    onClose();
  }, [project, onDispatch, onClose]);

  return (
    <div
      className="music-picker-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="music-picker-title"
    >
      <div className="music-picker-header">
        <span className="music-picker-title" id="music-picker-title">Add Music</span>
        <div className="music-picker-header-actions">
          <button
            className="music-picker-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingAudio}
            aria-label="Upload audio from device"
            aria-busy={isUploadingAudio}
            type="button"
          >
            {isUploadingAudio ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Upload size={14} aria-hidden="true" />
            )}
            <span>{isUploadingAudio ? "Uploading…" : "Upload"}</span>
          </button>
          <button
            className="timeline-toolbar-btn"
            onClick={onClose}
            aria-label="Close music picker"
          >
            <X size={16} />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.wav,.ogg"
          onChange={handleUploadAudio}
          className="hidden"
        />
      </div>

      {/* Search */}
      <div className="music-picker-search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search tracks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="music-picker-search-input"
        />
      </div>

      {/* Genre chips */}
      <div className="music-picker-genres">
        {GENRES.map((g) => (
          <button
            key={g}
            className={`music-picker-genre-btn ${genre === g.toLowerCase() ? "active" : ""}`}
            onClick={() => setGenre(genre === g.toLowerCase() ? null : g.toLowerCase())}
          >
            {g}
          </button>
        ))}
      </div>

      {/* Track list */}
      <div className="music-picker-list">
        {isLoading && <div className="music-picker-empty">Loading...</div>}
        {!isLoading && tracks.length === 0 && (
          <div className="music-picker-empty">No tracks found</div>
        )}
        {tracks.map((track) => (
          <div key={track.id} className="music-picker-track">
            <button
              className="music-picker-play-btn"
              onClick={() => togglePreview(track)}
            >
              {previewingId === track.id ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div className="music-picker-track-info">
              <span className="music-picker-track-title">{track.title}</span>
              <span className="music-picker-track-artist">{track.artist}</span>
            </div>
            <span className="music-picker-track-duration">
              {formatTimeShort(track.duration)}
            </span>
            <button
              className="music-picker-select-btn"
              onClick={() => handleSelect(track)}
            >
              <Check size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
